import React, { useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { Container } from '@mui/system';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import FlightItineraryRequestWizard from '~/components/FlightItineraryRequestWizard';
import HeroMenuButtons, { menuItem } from '~/components/HeroMenuButtons';
import { PAYMENT_STATUS } from '~/literals';
import { getItineraryOrder, startWizard } from '~/store/slices/itinerary-wizard';
import { RootState } from '~/types';

const ItineraryWizardRoute: React.FC = () => {
  const { orderId, paymentResult } = useParams<{ orderId: string; paymentResult?: 'success' | 'failed' }>();
  const location = useLocation();
  const dispatch = useDispatch();
  const wizard = useSelector((s: RootState) => s.itineraryWizard);
  const menuItems = [menuItem.searchFlight, menuItem.visaItinerary];
  const paymentStatus = new URLSearchParams(location.search).get('status');
  const isPaymentCanceled = paymentStatus === PAYMENT_STATUS.CANCELLED;

  useEffect(() => {
    if (isPaymentCanceled) return;

    if (orderId && paymentResult) {
      dispatch(getItineraryOrder({ orderId }));
      return;
    }

    if (orderId && wizard?.idempotencyKey !== orderId && wizard?.orderId !== orderId) {
      dispatch(startWizard({ idempotencyKey: orderId, packageId: undefined }));
    }
  }, [orderId, paymentResult, dispatch, wizard?.idempotencyKey, wizard?.orderId, isPaymentCanceled]);

  const matchesWizard = wizard && (wizard.idempotencyKey === orderId || wizard.orderId === orderId);
  const initialStep = paymentResult || isPaymentCanceled ? 4 : (matchesWizard ? wizard.step : 1);
  const initialPackageId = matchesWizard ? wizard.selectedPackageId || undefined : undefined;
  const pageTitle = paymentResult || isPaymentCanceled ? 'Your flight itinerary request is complete' : 'Complete your flight itinerary request';

  return (
    <Box sx={{ bgcolor: '#fff' }}>
      <Box
        color="#fff"
        sx={{
          backgroundImage: 'linear-gradient(rgb(15, 78, 146, .97), rgb(15, 78, 146, .97)), url(/media/content/itinerary.jpg)',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          position: 'relative',
          width: '100%',
        }}
      >
        <Container sx={{ py: { xs: 6, md: 8 } }}>
          <HeroMenuButtons menuItems={menuItems} />
          <Typography
            variant="h4"
            sx={{
              color: '#fff',
              fontWeight: 700,
              maxWidth: 760,
              mb: 1,
              mt: 3,
              textShadow: '0 2px 12px rgba(0,0,0,0.45)',
            }}
          >
            {pageTitle}
          </Typography>
        </Container>
      </Box>
      <Box sx={{ mt: 0 }}>
        <FlightItineraryRequestWizard initialStep={initialStep} initialPackageId={initialPackageId} paymentResult={isPaymentCanceled ? 'canceled' : paymentResult} />
      </Box>
    </Box>
  );
};

export default ItineraryWizardRoute;
